import React, { createContext, useContext, useEffect, useState } from "react"
import { AtticusScreenFlow } from "./models/AtticusScreenFlow"
import { User } from "./models/User"
import { Estate } from "./models/Estate"
import {
  SCREEN_PATHS,
  LARAVEL_APP_HOST,
  LARAVEL_APP_HOME,
  STORAGE,
  UPGRADE_REQUIRED,
  COOKIES,
  Roles,
  ADVISOR_APP_HOST,
} from "./constants"
import { estateApi, estateFormsApi, formsApi, geographiesApi } from "./apis"
import { AtticusMySQL } from "./apis/AtticusMySQL"
import { useNavigate, useLocation, NavigateFunction } from "react-router-dom"
import * as flows from "./views/flows"
import { Box } from "@mui/material"
import { AtticusCircularProgress } from "./views/shared/AtticusCircularProgress"
import { Language } from "./models/Langauges"
import { Subscription } from "./models/Subscription"
import { Country, County, CountyType, CourtType, State } from "./models/Geography"
import { AxiosError } from "axios"
import { getQueryStringParams } from "@theatticusapp/atticus-shared-components"
import { FormType } from "models/FormType"
import { LeadContext } from "LeadContextWrapper"
import { Auth0ContextInterface, useAuth0 } from "@auth0/auth0-react"
import { UserAPI } from "apis/UserAPI"
import Cookies from "js-cookie"
import { UserProfileAPI } from "apis/UserProfileAPI"
import { AtticusForm, FormTitleObject } from "models/AtticusForm"

interface Props {
  children: JSX.Element
}

interface AuthContextInterface {
  coreDataIsLoading: boolean
  flow?: AtticusScreenFlow
  setFlow: (flow?: AtticusScreenFlow) => void
  user?: User
  setUser: (user?: User) => void
  refreshEstates: (userId: number) => void
  estates?: Estate[] | null
  setEstates: (estates?: Estate[] | null) => void
  activeEstate?: Estate
  setActiveEstate: (estate?: Estate) => void
  formTypes?: FormType[]
  states?: State[]
  counties?: County[]
  countyTypes?: CountyType[]
  courtTypes?: CourtType[]
  countries?: Country[]
  languages?: Language[]
  subscription?: Subscription
  showContent: boolean
  setShowContent: (showContent: boolean) => void
  signOutUser: () => Promise<void>
  goToOnBoarding: () => void
  upgradeRequiredReason?: string
  setUpgradeRequiredReason: (reason: string) => void
  leadMigrated: boolean
  leadMigrationError?: string
  doneLoadingActiveEstate: boolean
  authToken: string | undefined
  avatarImageData?: string
}

const AuthContext = createContext<AuthContextInterface | null>(null)

enum StartUpTasks {
  PreloadData = "preloadData",
  FetchEstates = "fetchEstates",
  RecordForms = "recordForms",
}

// intentionally not using state as async nature causes problems with tasks tracking
let authTasks = [StartUpTasks.PreloadData, StartUpTasks.FetchEstates, StartUpTasks.RecordForms]

export async function authenticateViaAuth0(
  auth0: Auth0ContextInterface<any>,
  navigate: NavigateFunction
): Promise<[User, string] | undefined> {
  const params = getQueryStringParams()
  const redirectToSignup = params?.redirect_to === "signup"

  console.log("Email changed--loading user info")

  if (auth0.isLoading) {
    console.log("auth0 is loading")
    return
  }

  let token: string | undefined = undefined
  try {
    token = await auth0.getAccessTokenSilently()
  } catch (error) {
    console.error("error fetching token", error)
    if ((error as Error)?.message?.indexOf("verify your email") !== -1) {
      navigate(SCREEN_PATHS.emailVerificationRequired)
      return
    }
  }

  if (auth0.error) {
    console.error("error fetching token", auth0.error)
  }
  if (!token) {
    await auth0.loginWithRedirect({
      appState: {
        returnTo: window.location.origin,
      },
      authorizationParams: {
        screen_hint: redirectToSignup ? "signup" : "login",
      },
    })
    return
  }
  console.log("token found")

  if (!auth0?.user?.email) {
    console.error("we didn't find an email for this account")
    return
  }

  console.log("getting user from api")
  let myApiUser: User | undefined = await UserAPI.getUserByEmail(auth0.user.email, token)
  if (!myApiUser) {
    // If this user is actually an advisor, redirect them to the advisor site
    console.log("checking to see if user is an advisor")
    myApiUser = await UserAPI.getAdvisorByEmail(auth0.user.email, token)
  }
  if (
    myApiUser?.roleId === Roles.Advisor ||
    myApiUser?.roleId === Roles.Advisor_Admin ||
    myApiUser?.roleId === Roles.Super_Advisor_Admin ||
    myApiUser?.roleId === Roles.Uber_Admin
  ) {
    console.log("Logged-in user is an advisor, redirecting to advisor site")
    window.location.href = ADVISOR_APP_HOST
    return
  }

  if (!myApiUser) {
    console.error(
      "we have a valid auth0 login, but didn't find an email for this account and auth0 is done loading, redirecting to auto-signup"
    )
    if (window.location.pathname !== SCREEN_PATHS.autoSignup) {
      window.location.href = SCREEN_PATHS.autoSignup
    }
    return
  }

  console.log("found user " + myApiUser?.email + " with role " + myApiUser?.roleId)

  return [myApiUser, token]
}

export const AuthContextWrapper = ({ children }: Props): JSX.Element => {
  const navigate = useNavigate()
  const location = useLocation()
  const { doneLoadingLead } = useContext(LeadContext)
  const auth0 = useAuth0()
  const params = getQueryStringParams()
  const [coreDataIsLoading, setCoreDataIsLoading] = useState<boolean>(true)
  const [flow, setFlow] = useState<AtticusScreenFlow | undefined>(undefined)
  const [user, setUser] = useState<User | undefined>(undefined)
  const [estates, setEstates] = useState<Estate[] | undefined | null>(undefined)
  const [activeEstate, setActiveEstate] = useState<Estate | undefined>(undefined)
  const [countries, setCountries] = useState<Country[] | undefined>(undefined)
  const [counties, setCounties] = useState<County[] | undefined>(undefined)
  const [countyTypes, setCountyTypes] = useState<CountyType[] | undefined>(undefined)
  const [courtTypes, setCourtTypes] = useState<CourtType[] | undefined>(undefined)
  const [states, setStates] = useState<State[] | undefined>(undefined)
  const [formTypes, setFormTypes] = useState<FormType[] | undefined>()
  const [languages, setLanguages] = useState<Language[] | undefined>(undefined)
  const [subscription, setSubscription] = useState<Subscription | undefined>(undefined)
  const [authToken, setAuthToken] = useState<string | undefined>(undefined)
  const [dataLoaded, setDataLoaded] = useState<boolean>(false)
  const [showContent, setShowContent] = useState<boolean>(false)
  const [upgradeRequiredReason, setUpgradeRequiredReason] = useState<string | undefined>()
  const [leadMigrated, setLeadMigrated] = useState<boolean>(false)
  const [leadMigrationError, setLeadMigrationError] = useState<string | undefined>()
  const [doneLoadingActiveEstate, setDoneLoadingActiveEstate] = useState<boolean>(false)
  const [avatarImageData, setAvatarImageData] = useState<string | undefined>(undefined)
  const [checkedPaywall, setCheckedPaywall] = useState<boolean>(false)

  // forms of interest may come from sessionStorage - created pre-auth
  //  or they may come as query string params to any screen
  const formIdsFromQS = params?.formIds?.split(",") || []
  const formIdsFromStorageStr = window.sessionStorage.getItem(STORAGE.formInterest)
  const formIdsFromStorage = (formIdsFromStorageStr && JSON.parse(formIdsFromStorageStr)) || []
  const firstFreeFormId: string | undefined = params?.firstFormId
  const formsOfInterest = [...formIdsFromQS, ...formIdsFromStorage]
  if (firstFreeFormId) {
    formsOfInterest.push(firstFreeFormId)
  }

  useEffect(() => {
    if (params?.lead_forms === "true" && params?.redirect_to) {
      navigate(SCREEN_PATHS.leadMigration + "?redirect_to=" + params.redirect_to) // no need to encode--already url encoded
    }
  }, [params?.lead_forms, params?.redirect_to])

  useEffect(() => {
    ;(async () => {
      if (auth0.isLoading) {
        return
      }
      if (user && auth0.isAuthenticated) {
        return
      }
      const res = await authenticateViaAuth0(auth0, navigate)
      if (!res) {
        return
      }

      setUser(res[0])
      setAuthToken(res[1])
      Cookies.set(COOKIES.authToken, res[1]) // Pulled in by the axios instance

      if (res[0].hasCompletedOnboarding) {
        let paywall = true
        if (res[0]?.id) {
          const subscription = await AtticusMySQL.getSubscription(res[0]?.id)
          if (subscription?.isActive) {
            paywall = false
          }
        }
        if (paywall) {
          window.location.href = LARAVEL_APP_HOME + "?show_upgrade=true"
          return
        }
      }
      setCheckedPaywall(true)

      // console.log("navigating to home")
      if (window.location.pathname === SCREEN_PATHS.auth) {
        if (!res[0].hasCompletedOnboarding) {
          navigate(SCREEN_PATHS.onBoarding)
          return
        }
        window.location.href = LARAVEL_APP_HOME
        return
      }
    })()
  }, [auth0?.user?.email, auth0.isAuthenticated, auth0.isLoading])

  // Whenever all data is loaded, check to see if the user's route is permitted
  useEffect(() => {
    if (dataLoaded && doneLoadingLead) {
      checkRouting()
    }
  }, [dataLoaded, doneLoadingLead])

  // Once we have a user, pull their profile also
  useEffect(() => {
    ;(async () => {
      if (!user?.id) {
        return
      }
      const profile = await UserProfileAPI.get(user.id)
      const myUser = User.fromJSON(user.toJson())
      myUser.id = user.id
      myUser.profile = profile
      setUser(myUser)
    })()
  }, [user?.id])

  // Once we have a user and profile, check to see if they have an avatar
  useEffect(() => {
    ;(async () => {
      if (!user?.id) {
        return
      }
      if (!user?.profile?.profilePicBucketKey) {
        return
      }

      const avatar = await UserProfileAPI.getAvatar(
        user.id,
        user.profile.profilePicBucketKey,
        "200x200"
      )
      setAvatarImageData(avatar)
    })()
  }, [user, user?.profile, user?.profile?.profilePicBucketKey])

  useEffect(() => {
    ;(async () => {
      if (user?.id) {
        try {
          await fetchEstates(user.id)
        } catch (e) {
          console.error("AuthContextWrapper failed to load estate data, err=", e)
        }
      }
    })()
  }, [user?.id])

  // If the user changes, try to fetch the states and estates
  useEffect(() => {
    ;(async () => {
      if (!user?.id) {
        return
      }

      try {
        setCoreDataIsLoading(true)
        await Promise.all([
          geographiesApi.fetchCountries().then((countries) => setCountries(countries)),
          geographiesApi.fetchCountyTypes().then((countyTypes) => setCountyTypes(countyTypes)),
          geographiesApi.fetchCourtTypes().then((courtTypes) => setCourtTypes(courtTypes)),
          geographiesApi.fetchStates().then((states) => setStates(states)),
          AtticusMySQL.getLanguages().then((languages) => setLanguages(languages)),
          AtticusMySQL.getSubscription(user.id).then((subscription) =>
            setSubscription(subscription)
          ),
          formsApi.fetchFormTypes().then((formTypes) => setFormTypes(formTypes)),
        ])
      } catch (e) {
        console.error("AuthContextWrapper failed to load core data, err=" + e)
      }

      markTaskComplete(StartUpTasks.PreloadData)
      setDataLoaded(true)
      setCoreDataIsLoading(false)
    })()
  }, [user?.id])

  // once we have an active estate, record forms of interest
  useEffect(() => {
    ;(async () => {
      if (activeEstate?.id) {
        recordFormsOfInterest()
      }
    })()
  }, [activeEstate?.id])

  const markTaskComplete = (taskName: StartUpTasks) => {
    authTasks = authTasks.filter((task: string) => task !== taskName.toString())
    // once all startup tasks are complete checkRouting
    if (authTasks?.length === 0) {
      checkRouting()
    }
  }

  const signOutUser = async (): Promise<void> => {
    try {
      setUser?.(undefined)
      setEstates?.(undefined)
      setAuthToken(undefined)
      Cookies.remove(COOKIES.authToken)
      await auth0.logout({ logoutParams: { returnTo: LARAVEL_APP_HOST + "/sign-out" } })
    } catch (error) {
      console.error(error)
    }
  }

  const refreshEstates = async (userId: number): Promise<void> => {
    await fetchEstates(userId)
  }

  const fetchEstates = async (userId: number): Promise<void> => {
    const fetchedEstates = await estateApi.fetchEstates(userId)

    // we have an activeEstate
    if (fetchedEstates?.length && fetchedEstates[0].id) {
      setEstates(fetchedEstates)
      fetchedEstates[0].assets = await estateApi.getAssets(fetchedEstates[0].id, userId)
      setActiveEstate(fetchedEstates[0])
    } else {
      // if no estates, activeEstate won't be set
      //  must mark recordForms complete here
      markTaskComplete(StartUpTasks.RecordForms)
    }
    markTaskComplete(StartUpTasks.FetchEstates)
    setDoneLoadingActiveEstate(true)
  }

  const recordFormsOfInterest = async (): Promise<void> => {
    if (formsOfInterest?.length && activeEstate?.id && user?.id) {
      for (let i = 0; i < formsOfInterest?.length; i++) {
        const formId = parseInt(formsOfInterest[i], 10)
        try {
          await estateFormsApi.addFormToEstate(
            activeEstate.id,
            user.id,
            new AtticusForm(formId, {} as FormTitleObject)
          ) // just needs form id
        } catch (error) {
          if ((error as AxiosError)?.response?.status === 402) {
            setUpgradeRequiredReason(UPGRADE_REQUIRED.maxForms)
          }
        }
      }
      // clear the storage item so they're not added again
      window.sessionStorage.removeItem(STORAGE.formInterest)
    }
    markTaskComplete(StartUpTasks.RecordForms)
  }

  /**
   * Checks the current user's URL route to determine if they should be
   * redirected to another part of the app.
   *
   * When Redirects should occur:
   *    requested any path (other than on-boarding) when on-boarding not complete   => on-boarding
   *    requested a disabled flow                                                   => Vue home
   *    requested on-boarding and yet it's marked complete                          => Vue home
   *    requested a disabled path (constants DISABLED_PATHS)                        => Vue home
   *    requested Forms or Probate Packet paths when AG doesn't allow           => Vue home
   *    unrecognized paths will be sent to 404 (via react-router)                   => 404
   */
  const checkRouting = (): void => {
    // const requestingFlow = location.pathname.includes(SCREEN_PATHS.flow, 0);
    // const requestingAccountCreation = location.pathname == SCREEN_PATHS.createAccountAndRedirect
    // const requestingOnBoarding = location.pathname == SCREEN_PATHS.onBoarding;

    // // if on-boarding is incomplete go to on-boarding
    // if (!requestingAccountCreation && !user?.hasCompletedOnboarding && !requestingOnBoarding) {
    //   goToOnBoarding(true);
    //   return;
    // }

    // // if requesting a flow check if customer should be in there
    // if (requestingFlow) {
    //   // requesting a disabled flow?
    //   const flowPath = location.pathname.split("/")[2];
    //   const flow = Object.values(flows).find(
    //     (flow) => flow.flowPath === flowPath
    //   );
    //   const flowDisabled = flow?.disabled;

    //   // requesting on-boarding when it's complete
    //   const preventOnBoarding = !!lead?.key || (
    //     location.pathname.includes(SCREEN_PATHS.onBoarding) &&
    //     user?.hasCompletedOnboarding)

    //   if (flowDisabled || preventOnBoarding) {
    //     goToVueAppHome();
    //     return;
    //   }
    // }

    // // If route is forms or probate packet determination, check if AG allows it.
    // const requestingFormsOrProbatePacket = location.pathname
    //   .split("/")[2]
    //   .toLowerCase()
    //   .includes("forms-and-files");
    // if (requestingFormsOrProbatePacket) {
    //   const formsDisabled = activeEstate?.advisorGroup?.formsDisabled;
    //   if (formsDisabled) {
    //     goToVueAppHome();
    //     return;
    //   }
    // }

    // // requesting a disabled path? redirect to vue
    // if (DISABLED_PATHS.includes(location.pathname)) {
    //   goToVueAppHome();
    //   return;
    // }

    // -----------------------------------------------------------------------------
    //  IF YOU MAKE IT THIS FAR, SHOW CONTENT TO USER
    // -----------------------------------------------------------------------------

    setShowContent(true)
  }

  /**
   * Take the user to the Onboarding Flow.
   * If a user is already in the Onboarding flow, display the screen they are accessing
   * If a user is not already in the Onboarding flow, route them to the first screen of Onboarding.
   */
  const goToOnBoarding = (forceFirst?: boolean): void => {
    const alreadyInOnboardingFlow = location.pathname.includes(SCREEN_PATHS.onBoarding, 0)

    if (!alreadyInOnboardingFlow || forceFirst) {
      navigate(flows.onBoardingFlow.getFlowFirstScreen())
    }
    setShowContent(true)
  }

  return (
    <AuthContext.Provider
      value={{
        coreDataIsLoading,
        flow,
        setFlow,
        user,
        setUser,
        refreshEstates,
        estates,
        setEstates,
        activeEstate,
        setActiveEstate,
        doneLoadingActiveEstate,
        formTypes,
        states,
        counties,
        countyTypes,
        courtTypes,
        countries,
        languages,
        subscription,
        showContent,
        setShowContent,
        signOutUser,
        upgradeRequiredReason,
        setUpgradeRequiredReason,
        leadMigrated,
        leadMigrationError,
        authToken,
        goToOnBoarding,
        avatarImageData,
      }}
    >
      {checkedPaywall && showContent && children}
      {(!checkedPaywall || !showContent) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
          }}
        >
          <AtticusCircularProgress />
        </Box>
      )}
    </AuthContext.Provider>
  )
}

export function useAuthContext(): AuthContextInterface {
  const authContext = useContext(AuthContext)
  if (!authContext) {
    throw new Error(
      "the component calling this auth context is not nested within a context provider"
    )
  }

  return authContext
}
